import React, { useCallback, useEffect, useRef, useState } from 'react';
import './formatting-tools.css';
export default function FormattingTools({ editorRef, restore, capture, emit, exec, onAnnotationSelect }) {
  const selected = useRef(null);
  const [drawMode, setDrawMode] = useState(false);
  const selectAnnotation = useCallback(node => {
    const root = editorRef.current;
    if (!root) return;
    if (selected.current && root.contains(selected.current)) selected.current.classList.remove('rte-annotation-selected');
    selected.current = node && root.contains(node) ? node : null;
    selected.current?.classList.add('rte-annotation-selected');
    onAnnotationSelect?.(selected.current);
  }, [editorRef, onAnnotationSelect]);
  useEffect(() => {
    const root = editorRef.current;
    if (!root) return undefined;
    const select = event => {
      const target = event.target.closest?.('.rte-annotation');
      selectAnnotation(target);
    };
    const onKeyUp = () => {
      const node = window.getSelection()?.anchorNode;
      const element = node?.nodeType === 1 ? node : node?.parentElement;
      selectAnnotation(element?.closest?.('.rte-annotation'));
    };
    // Pointer down fires before a text drag starts; click alone may never
    // fire after a drag selection inside a box.
    root.addEventListener('pointerdown', select);
    root.addEventListener('click', select);
    root.addEventListener('keyup', onKeyUp);
    return () => { root.removeEventListener('pointerdown', select); root.removeEventListener('click', select); root.removeEventListener('keyup', onKeyUp); };
  }, [editorRef, selectAnnotation]);
  useEffect(() => {
    const root = editorRef.current;
    if (!root || !drawMode) return undefined;
    root.classList.add('rte-draw-mode');
    let cleanupDrag = null;
    let pendingArrow = null;
    const onDown = event => {
      if (event.button !== 0 || event.target.closest?.('.rte-annotation')) return;
      event.preventDefault();
      let range = document.caretRangeFromPoint?.(event.clientX, event.clientY);
      if (!range) {
        range = document.createRange();
        const caret = document.caretPositionFromPoint?.(event.clientX, event.clientY);
        if (caret) range.setStart(caret.offsetNode, caret.offset);
      }
      if (!root.contains(range.commonAncestorContainer)) { range.selectNodeContents(root); range.collapse(false); }
      range.collapse(true);
      const anchor = range.commonAncestorContainer.nodeType === 1
        ? range.commonAncestorContainer : range.commonAncestorContainer.parentElement;
      const existing = anchor?.closest('.rte-annotation');
      if (existing && root.contains(existing)) { range.setStartAfter(existing); range.collapse(true); }
      const arrow = document.createElement('span');
      arrow.className = 'rte-annotation rte-arrow rte-drawn';
      arrow.style.width = '16px';
      arrow.style.transform = 'rotate(0deg)';
      arrow.textContent = '→';
      range.insertNode(arrow);
      pendingArrow = arrow;
      selectAnnotation(arrow);
      const startX = event.clientX, startY = event.clientY, pointerId = event.pointerId;
      const move = pointer => {
        if (pointer.pointerId !== pointerId) return;
        const dx = pointer.clientX - startX, dy = pointer.clientY - startY;
        arrow.style.width = `${Math.min(600, Math.max(16, Math.hypot(dx, dy)))}px`;
        arrow.style.transform = `rotate(${Math.round(Math.atan2(dy, dx) * 180 / Math.PI)}deg)`;
      };
      const finish = pointer => {
        if (pointer.pointerId !== pointerId) return;
        move(pointer);
        cleanupDrag?.();
        cleanupDrag = null;
        if (Math.hypot(pointer.clientX - startX, pointer.clientY - startY) < 15) arrow.remove();
        else emit();
        pendingArrow = null;
        setDrawMode(false);
      };
      const cancel = () => { arrow.remove(); pendingArrow = null; cleanupDrag?.(); cleanupDrag = null; setDrawMode(false); };
      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', finish);
      document.addEventListener('pointercancel', cancel);
      cleanupDrag = () => {
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', finish);
        document.removeEventListener('pointercancel', cancel);
      };
    };
    const onKey = event => { if (event.key === 'Escape') setDrawMode(false); };
    root.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { pendingArrow?.remove(); root.classList.remove('rte-draw-mode'); root.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); cleanupDrag?.(); };
  }, [drawMode, editorRef, emit, selectAnnotation]);
  const blockStyle = (property, value) => {
    const root = editorRef.current;
    if (!root) return;
    root.focus(); restore();
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;
    const blockSelector = 'p,div,li,h1,h2,h3,blockquote';
    // Style the text blocks the user selected, not their enclosing page
    // container (or an annotation nested inside a paragraph).
    let blocks = [...root.querySelectorAll(blockSelector)].filter(el =>
      !el.closest('.rte-annotation') &&
      !el.querySelector(blockSelector) &&
      range.intersectsNode(el)
    );
    if (!blocks.length) {
      exec('formatBlock', 'p');
      const node = window.getSelection()?.anchorNode;
      const block = (node?.nodeType === 1 ? node : node?.parentElement)?.closest(blockSelector);
      if (block && root.contains(block) && block !== root) blocks = [block];
    }
    blocks.forEach(el => { el.style[property] = value; }); emit();
  };
  const insert = (arrow) => {
    const root = editorRef.current;
    if (!root) return;
    root.focus();
    restore();
    const selection = window.getSelection();
    let range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    if (!range || !root.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(root);
      range.collapse(false);
    }
    const ancestor = range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer : range.commonAncestorContainer.parentElement;
    const currentBox = ancestor?.closest('.rte-annotation');
    if (currentBox && root.contains(currentBox)) {
      range.setStartAfter(currentBox);
      range.collapse(true);
    }
    range.deleteContents();
    // execCommand('insertHTML') silently flattened a block text box into
    // plain text when the caret was inside a paragraph. A span can live
    // safely inside that paragraph while retaining its class and styles.
    const box = document.createElement('span');
    box.className = arrow ? 'rte-annotation rte-arrow' : 'rte-annotation';
    box.style.left = '0%';
    box.style.top = '0px';
    box.style.width = arrow ? '20%' : '50%';
    if (arrow) box.style.transform = 'rotate(0deg)';
    box.textContent = arrow ? '→' : 'Type your text here';
    range.insertNode(box);
    selectAnnotation(box);
    const next = document.createRange();
    if (arrow) next.setStartAfter(box);
    else next.selectNodeContents(box);
    if (arrow) next.collapse(true);
    selection.removeAllRanges();
    selection.addRange(next);
    capture?.();
    emit();
  };
  const annotation = () => {
    const node = window.getSelection()?.anchorNode;
    const element = (node?.nodeType === 1 ? node : node?.parentElement)?.closest('.rte-annotation');
    if (element && editorRef.current.contains(element)) selectAnnotation(element);
    return selected.current && editorRef.current.contains(selected.current) ? selected.current : null;
  };
  const adjust = (property, amount, unit, initial = 0) => {
    restore(); const el = annotation(); if (!el) return;
    if (property === 'width' && el.classList.contains('rte-drawn')) {
      el.style.width = Math.max(16, Math.min(600, (parseFloat(el.style.width) || 80) + amount * 4)) + 'px';
      emit();
      return;
    }
    if (property === 'width' && el.classList.contains('rte-arrow')) {
      el.style.fontSize = Math.max(20, Math.min(120, (parseFloat(el.style.fontSize) || 40) + Math.sign(amount) * 8)) + 'px';
      emit();
      return;
    }
    el.style[property] = Math.max(property === 'width' ? 10 : -100, Math.min(100, (parseFloat(el.style[property]) || initial) + amount)) + unit;
    emit();
  };
  const boxStyle = (kind, value) => {
    restore();
    const el = annotation();
    if (!el || el.classList.contains('rte-arrow')) return;
    const prefix = kind === 'border' ? 'rte-box-border-' : 'rte-box-fill-';
    [...el.classList].filter(name => name.startsWith(prefix)).forEach(name => el.classList.remove(name));
    if (value !== 'default') el.classList.add(prefix + value);
    emit();
  };
  const alignBoxText = value => {
    restore();
    const el = annotation();
    if (!el || el.classList.contains('rte-arrow')) return;
    el.style.textAlign = value;
    emit();
  };
  const alignBox = value => {
    restore();
    const el = annotation();
    if (!el || el.classList.contains('rte-arrow')) return;
    // Position the *element*, not its text. Classes survive the page HTML
    // sanitizer, which intentionally does not allow margin-left/right styles.
    el.style.left = '0%';
    el.classList.remove('rte-box-position-left', 'rte-box-position-center', 'rte-box-position-right');
    el.classList.add(`rte-box-position-${value}`);
    emit();
  };
  return <div className="formatting-tools" role="group" aria-label="Paragraph and annotation formatting" onMouseDown={e => { if (e.target.tagName === 'BUTTON') e.preventDefault(); }}>
    <div className="formatting-spacing"><label className="admin-field"><span className="admin-label">Line spacing</span><select className="admin-select" aria-label="Line spacing" defaultValue="" onChange={e => blockStyle('lineHeight', e.target.value)}><option value="" disabled>Choose</option>{[1, 1.15, 1.5, 1.8, 2].map(n => <option key={n}>{n}</option>)}</select></label>
    {['Before', 'After'].map((label, i) => <label key={label} className="admin-field"><span className="admin-label">Paragraph {label.toLowerCase()}</span><select className="admin-select" aria-label={`Paragraph ${label.toLowerCase()}`} defaultValue="" onChange={e => blockStyle(i ? 'marginBottom' : 'marginTop', e.target.value + 'px')}><option value="" disabled>Choose</option>{[0, 4, 8, 12, 16, 24, 32, 40, 48, 64, 80, 96, 120].map(n => <option key={n} value={n}>{n}px</option>)}</select></label>)}
    <label className="admin-field"><span className="admin-label">First-line indent</span><select className="admin-select" aria-label="First-line indent" defaultValue="" onChange={e => blockStyle('textIndent', e.target.value)}><option value="" disabled>Choose</option>{['0em', '1em', '2em', '3em'].map(n => <option key={n} value={n}>{n}</option>)}</select></label>
    </div><div className="formatting-actions">
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => exec('insertUnorderedList')}>Bullets</button><button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => exec('insertOrderedList')}>Numbered list</button>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => { exec('insertOrderedList'); const n = window.getSelection()?.anchorNode; const list = (n?.nodeType === 1 ? n : n?.parentElement)?.closest('ol'); if (list) list.style.listStyleType = 'upper-alpha'; emit(); }}>Lettered list</button>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => exec('insertHorizontalRule')}>Separator</button>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => insert(false)}>Text box</button><button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => insert(true)}>Arrow</button>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" aria-pressed={drawMode} onClick={() => setDrawMode(value => !value)}>{drawMode ? 'Cancel drawing' : 'Draw arrow'}</button>
    </div><div className="formatting-annotations"><span className="formatting-hint">{drawMode ? 'Drag across the text area to draw an arrow. Press Escape to cancel.' : 'Select a text box or arrow to adjust'}</span><div className="formatting-actions">
    <span className="formatting-annotation-label">Position box</span>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => alignBox('left')}>Box left</button><button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => alignBox('center')}>Box center</button><button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => alignBox('right')}>Box right</button>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" aria-label="Nudge annotation left" onClick={() => adjust('left', -2, '%')}>Nudge left</button><button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" aria-label="Nudge annotation right" onClick={() => adjust('left', 2, '%')}>Nudge right</button>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" aria-label="Move annotation up" onClick={() => adjust('top', -5, 'px')}>Up</button><button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" aria-label="Move annotation down" onClick={() => adjust('top', 5, 'px')}>Down</button>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => adjust('width', -5, '%', 50)}>Narrower</button><button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => adjust('width', 5, '%', 50)}>Wider</button>
    <span className="formatting-annotation-label">Text in box</span>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => alignBoxText('left')}>Align box text left</button>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => alignBoxText('center')}>Align box text center</button>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => alignBoxText('right')}>Align box text right</button>
    <select className="admin-select formatting-annotation-select" aria-label="Text box border" defaultValue="" onChange={e => { boxStyle('border', e.target.value); e.target.value = ''; }}><option value="" disabled>Box border...</option><option value="default">Default</option><option value="none">None</option><option value="gold">Gold</option><option value="blue">Blue</option><option value="green">Green</option><option value="red">Red</option><option value="purple">Purple</option><option value="brown">Brown</option><option value="black">Black</option><option value="grey">Grey</option><option value="white">White</option><option value="gold-thick">Gold thick</option><option value="gold-dashed">Gold dashed</option><option value="gold-dotted">Gold dotted</option><option value="double">Double</option></select>
    <select className="admin-select formatting-annotation-select" aria-label="Text box fill" defaultValue="" onChange={e => { boxStyle('fill', e.target.value); e.target.value = ''; }}><option value="" disabled>Box fill...</option><option value="default">None</option><option value="cream">Cream</option><option value="blue">Blue</option><option value="green">Green</option><option value="yellow">Yellow</option><option value="peach">Peach</option><option value="pink">Pink</option><option value="lavender">Lavender</option><option value="aqua">Aqua</option><option value="sand">Sand</option><option value="grey">Grey</option><option value="white">White</option><option value="navy">Navy</option><option value="forest">Forest</option><option value="maroon">Maroon</option><option value="charcoal">Charcoal</option></select>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => { restore(); const el = annotation(); if (el?.classList.contains('rte-arrow')) { const degrees = Number(el.style.transform.match(/-?\d+/)?.[0] || 0) + 15; el.style.transform = `rotate(${degrees % 360}deg)`; emit(); } }}>Rotate arrow</button>
    <button className="admin-btn admin-btn-ghost admin-btn-sm" type="button" onClick={() => { restore(); annotation()?.remove(); selectAnnotation(null); emit(); }}>Remove annotation</button>
    </div></div>
  </div>;
}
